// PrintHQ Cloud: the account's entitlement (the server's answer, never
// decided here), flags, devices, sync, storage, restore points,
// notifications and feedback. Everything goes through RLS-scoped reads, the
// SECURITY DEFINER functions of the 20261103 migrations, or the vault-api /
// billing-plans / feedback functions. Contract: docs/ENTITLEMENT_CONTRACT.md.
//
// In demo mode (no Supabase configured) these answer with sample data so the
// screens can be seen; nothing is stored.
import { isDemo } from '@/services/cloudDemo'
import * as demo from '@/services/cloudDemo'

export type EntitlementState =
  | 'trial_available' | 'trial_active' | 'trial_expired'
  | 'subscription_active' | 'subscription_past_due' | 'subscription_canceled' | 'subscription_expired'
  | 'access_revoked' | 'admin_granted' | 'grandfathered' | 'no_entitlement'

export interface Entitlement {
  version: 1
  state: EntitlementState
  entitled: boolean
  plan: 'maker' | null
  source: string | null
  access_until: string | null
  reason: string | null
  trial: {
    eligibility: 'eligible' | 'ineligible' | 'admin_review' | null
    available: boolean
    can_start: boolean
    activated_at: string | null
    expires_at: string | null
    duration_hours: number
    consumed: boolean
  }
  subscription: {
    id: string | null
    status: string
    plan: string
    interval: 'month' | 'year' | null
    current_period_end: string | null
    cancel_at_period_end: boolean
    canceled_at: string | null
    ended_at: string | null
  } | null
  grant: { kind: string; ends_at: string | null } | null
  other_plan: 'hobby' | null
  revocation_version: number
  limits: { storage_gb: number; uploads_allowed: boolean; scenes_limit: number | null; scenes_period: 'trial' | 'month' | null }
  retention: { policy: string }
  computed_at: string
}

export type Flags = Record<string, boolean>

export interface PlanPrice { price_id: string; amount: number; currency: string; interval: 'month' | 'year' }
export interface Plans { maker: { month: PlanPrice | null; year: PlanPrice | null; yearly_saving_pct: number | null } }

export interface Device {
  id: string
  kind: 'desktop' | 'sync_device'
  device_name: string
  platform: string | null
  app_version: string | null
  registered_at: string
  last_seen_at: string
  last_verified_at: string | null
  last_sync_at: string | null
  auth_status: 'active' | 'signed_out' | 'revoked' | 'stale'
  revoked_reason: string | null
  capabilities: Record<string, boolean>
  sync_device_id: string | null
  sync: {
    sync_enabled: boolean
    synced_models: number
    pending: number
    failed: number
    conflicts: number
    last_error: string | null
    last_attempt_at: string | null
    reported_at: string
    retry_requested_at: string | null
    retry_acknowledged_at: string | null
  } | null
}

export interface SyncStatusRow {
  device_id: string
  activation_id: string | null
  failed_items: { object_id: string | null; model_id: string | null; file_name: string | null; code: string; message: string | null; at: string | null }[]
  conflicts: { object_id: string; model_id: string | null; file_name: string | null; remote_version: number | null; detected_at: string | null }[]
  last_success_at: string | null
  reported_at: string
}

export interface Quota { tier: string; limit_bytes: number; used_bytes: number; vault_used_bytes: number; files_used_bytes: number; versions_retained: number; uploads_allowed?: boolean }
export interface StorageBreakdown { vault: Record<string, { bytes: number; files: number }>; web_files_bytes: number }

export interface RestorePoint {
  object_id: string
  version: number
  is_current: boolean
  file_name: string
  file_ext: string | null
  size_bytes: number
  content_hash: string
  created_at: string
  device_id: string | null
  device_name: string | null
  model_id: string | null
  model_name: string | null
  folder_path: string | null
}

export interface ActivityRow { id: number; device_id: string | null; device_name: string | null; action: string; object_id: string | null; model_id: string | null; detail: Record<string, unknown> | null; created_at: string }

export interface CloudNotification { id: string; kind: string; title: string; body: string; link: string | null; severity: 'info' | 'success' | 'warning' | 'error'; read_at: string | null; created_at: string }

export type SyncCategories = Record<'model_metadata' | 'model_files' | 'thumbnails' | 'filament', boolean>

async function sb() {
  return (await import('@/services/supabase')).supabase
}

/** An edge function's error, with the server's own message. */
async function fnError(error: unknown): Promise<Error & { code?: string }> {
  const ctx = (error as { context?: Response }).context
  let message = (error as Error).message
  let code: string | undefined
  try {
    const j = ctx ? await ctx.json() : null
    if (j?.error) message = j.error
    code = j?.code
  } catch {
    /* keep the generic message */
  }
  return Object.assign(new Error(message), { code })
}

export async function vaultApi<T>(path: string, body?: unknown, method: 'GET' | 'POST' = 'POST'): Promise<T> {
  const { data, error } = await (await sb()).functions.invoke(`vault-api${path}`, { method, body: body as Record<string, unknown> | undefined })
  if (error) throw await fnError(error)
  return data as T
}

async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await (await sb()).rpc(fn as never, args as never)
  if (error) throw new Error(error.message)
  return data as T
}

export const cloud = {
  /** The entitlement (refresh: re-read Stripe first if it may be stale, e.g. after Checkout). */
  async entitlement(refresh = false): Promise<{ entitlement: Entitlement; flags: Flags }> {
    if (isDemo()) return { entitlement: demo.entitlement(), flags: demo.flags() }
    return vaultApi(`/entitlement${refresh ? '?refresh=1' : ''}`, undefined, 'GET')
  },
  /** Flags for anyone (the pricing page works signed out). */
  async flags(): Promise<Flags> {
    if (isDemo()) return demo.flags()
    return rpc<Flags>('my_flags')
  },
  /** "Start My 7-Day Free Trial": the same server function Vault uses. */
  async startTrial(): Promise<{ ok: boolean; code: string; entitlement: Entitlement }> {
    if (isDemo()) return demo.startTrial()
    return rpc('start_my_trial')
  },
  async plans(): Promise<Plans> {
    if (isDemo()) return demo.plans()
    const { data, error } = await (await sb()).functions.invoke('billing-plans', { method: 'GET' })
    if (error) throw await fnError(error)
    return data as Plans
  },
  /** Hosted Stripe Checkout for Maker; the price is the server's. */
  async checkout(interval: 'month' | 'year', source: string, affiliateCode?: string): Promise<{ url?: string; already_subscribed?: boolean; portal_url?: string | null }> {
    if (isDemo()) throw new Error('Checkout isn’t available in the demo.')
    const { data, error } = await (await sb()).functions.invoke('create-checkout-session', {
      body: { plan: 'maker', interval, source, affiliateCode: affiliateCode || undefined },
    })
    if (error) throw await fnError(error)
    return data
  },
  async portal(): Promise<string> {
    if (isDemo()) throw new Error('Billing isn’t available in the demo.')
    return (await vaultApi<{ url: string }>('/billing/portal')).url
  },

  async devices(): Promise<Device[]> {
    if (isDemo()) return demo.devices()
    return rpc<Device[]>('my_devices')
  },
  async syncStatus(): Promise<SyncStatusRow[]> {
    if (isDemo()) return demo.syncStatus()
    const { data, error } = await (await sb()).from('vault_device_sync_status' as never)
      .select('device_id, activation_id, failed_items, conflicts, last_success_at, reported_at')
    if (error) throw new Error(error.message)
    return (data ?? []) as unknown as SyncStatusRow[]
  },
  renameDevice: (id: string, name: string) => (isDemo() ? Promise.resolve() : rpc<void>('rename_my_device', { p_id: id, p_name: name })),
  revokeDevice: (id: string) => (isDemo() ? Promise.resolve({ revoked: true, session_ended: true }) : rpc<{ revoked: boolean; session_ended: boolean }>('revoke_my_device', { p_id: id })),
  reauthorizeDevice: (id: string) => (isDemo() ? Promise.resolve() : rpc<void>('reauthorize_my_device', { p_id: id })),
  removeDevice: (id: string) => (isDemo() ? Promise.resolve() : rpc<void>('remove_my_device', { p_id: id })),
  retrySync: (syncDeviceId: string) => (isDemo() ? Promise.resolve() : rpc<void>('request_device_sync_retry', { p_sync_device: syncDeviceId })),

  async syncCategories(): Promise<SyncCategories> {
    if (isDemo()) return demo.syncCategories()
    return rpc<SyncCategories>('my_sync_categories')
  },
  setSyncCategory: (c: keyof SyncCategories, on: boolean) =>
    isDemo() ? Promise.resolve({ ...demo.syncCategories(), [c]: on }) : rpc<SyncCategories>('set_my_sync_category', { p_category: c, p_enabled: on }),

  async quota(): Promise<Quota> {
    if (isDemo()) return demo.quota()
    return vaultApi<Quota>('/quota', undefined, 'GET')
  },
  async storageBreakdown(): Promise<StorageBreakdown> {
    if (isDemo()) return demo.breakdown()
    return rpc<StorageBreakdown>('my_storage_breakdown')
  },
  async restorePoints(opts: { limit?: number; before?: string | null; search?: string | null } = {}): Promise<RestorePoint[]> {
    if (isDemo()) return demo.restorePoints()
    return rpc<RestorePoint[]>('my_restore_points', { p_limit: opts.limit ?? 100, p_before: opts.before ?? null, p_search: opts.search || null })
  },
  async downloadVersion(objectId: string, version: number): Promise<{ url: string; file_name: string }> {
    if (isDemo()) throw new Error('Downloads aren’t available in the demo.')
    return vaultApi('/objects/download-url', { object_id: objectId, version })
  },
  async restoreVersion(objectId: string, version: number): Promise<{ version: number; restored_from: number } | { already_current: true }> {
    if (isDemo()) return { version: version + 1, restored_from: version }
    return vaultApi('/objects/restore-version', { object_id: objectId, version })
  },
  async activity(limit = 50, before?: number): Promise<ActivityRow[]> {
    if (isDemo()) return demo.activity()
    return (await vaultApi<{ activity: ActivityRow[] }>(`/activity?limit=${limit}${before ? `&before=${before}` : ''}`, undefined, 'GET')).activity
  },

  async notifications(limit = 30): Promise<CloudNotification[]> {
    if (isDemo()) return demo.notifications()
    const { data, error } = await (await sb()).from('notifications' as never)
      .select('id, kind, title, body, link, severity, read_at, created_at')
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) throw new Error(error.message)
    return (data ?? []) as unknown as CloudNotification[]
  },
  async markRead(ids: string[]) {
    if (isDemo() || ids.length === 0) return
    const { error } = await (await sb()).from('notifications' as never).update({ read_at: new Date().toISOString() } as never).in('id', ids)
    if (error) throw new Error(error.message)
  },
  async notificationPrefs(): Promise<Record<string, boolean>> {
    if (isDemo()) return demo.notificationPrefs()
    return rpc('my_notification_preferences')
  },
  setNotificationPref: (kind: string, email: boolean) =>
    isDemo() ? Promise.resolve({ ...demo.notificationPrefs(), [kind]: email }) : rpc<Record<string, boolean>>('set_my_notification_preference', { p_kind: kind, p_email: email }),

  /** Feedback: confirmed only when the server answers with an id. */
  async sendFeedback(body: Record<string, unknown>): Promise<{ id: string; duplicate?: boolean }> {
    if (isDemo()) return { id: crypto.randomUUID() }
    const { data, error } = await (await sb()).functions.invoke('feedback', { body })
    if (error) throw await fnError(error)
    if (!data?.id) throw new Error('Your feedback couldn’t be confirmed. Please try again.')
    return data
  },
  async setPrompt(prompt: 'trial_expiry' | 'cancellation', state: 'shown' | 'dismissed' | 'submitted') {
    if (isDemo()) return
    await rpc('feedback_prompt_set', { p_prompt: prompt, p_state: state, p_app: 'web' })
  },
  async promptState(prompt: 'trial_expiry' | 'cancellation'): Promise<string | null> {
    if (isDemo()) return null
    const { data } = await (await sb()).from('feedback_prompts' as never).select('state').eq('prompt', prompt).maybeSingle()
    return (data as { state?: string } | null)?.state ?? null
  },
}

// ---------------------------------------------------------------------------
// Wording shared by the screens (status is never shown by color alone)
// ---------------------------------------------------------------------------

export function money(p: PlanPrice | null): string {
  if (!p) return '—'
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: p.currency.toUpperCase(), minimumFractionDigits: p.amount % 100 === 0 ? 0 : 2 }).format(p.amount / 100)
}

export function bytes(n: number | null | undefined): string {
  if (n == null) return '—'
  const u = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`
}

export function timeLeft(until: string | null, now = Date.now()): string {
  if (!until) return ''
  const s = Math.max(0, (Date.parse(until) - now) / 1000)
  if (s <= 0) return 'ended'
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  if (d >= 1) return `${d} ${d === 1 ? 'day' : 'days'}${d < 3 && h ? `, ${h} ${h === 1 ? 'hour' : 'hours'}` : ''} left`
  if (h >= 1) return `${h} ${h === 1 ? 'hour' : 'hours'} left`
  const m = Math.max(1, Math.floor(s / 60))
  return `${m} ${m === 1 ? 'minute' : 'minutes'} left`
}

export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'never'
  const s = Math.round((now - Date.parse(iso)) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)} min ago`
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`
  if (s < 30 * 86400) return `${Math.floor(s / 86400)} d ago`
  return new Date(iso).toLocaleDateString()
}

export const STATE_LABELS: Record<EntitlementState, string> = {
  trial_available: 'Free trial available',
  trial_active: 'Free trial',
  trial_expired: 'Trial ended',
  subscription_active: 'Maker',
  subscription_past_due: 'Payment issue',
  subscription_canceled: 'Maker (canceled)',
  subscription_expired: 'Subscription ended',
  access_revoked: 'Access turned off',
  admin_granted: 'Maker (granted)',
  grandfathered: 'Maker',
  no_entitlement: 'Free account',
}
